import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Grid,
  Paper,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import ImageUpload from "../ImageUpload";
import styled from "styled-components/macro";
import AddIcon from "@material-ui/icons/Add";
import { useSnackbar } from "notistack";
import { useAppContext } from "../../api/AppContext";

const GalleryContainer = styled.div`
  padding: 2rem;
  width: 100%;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: ${({ center }) => (center ? "center" : "flex-start")};
  align-items: center;
  width: 100%;
`;

const ImageCard = styled(Paper)`
  padding: 12px;
  min-height: fit-content;
  height: 12rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center !important;
`;

const ImageInfo = styled.div`
  width: 100%;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 4px;
`;

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const TeamsGallery = () => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    teamsGalleryImages = [],
    isTeamsGalleryImagesLoading,
    fetchTeamsGalleryImages,
    updateTeamsGalleryImage,
    deleteTeamsGalleryImage,
  } = useAppContext();

  const [loading, setLoading] = useState(false);
  const [uploadingImageId, setUploadingImageId] = useState(null);
  const [newImages, setNewImages] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);

  useEffect(() => {
    fetchTeamsGalleryImages();
  }, [fetchTeamsGalleryImages]);

  const handleDirectUpload = useCallback(
    async ({ url }) => {
      try {
        setUploadingImageId("direct-upload");
        setLoading(true);

        await updateTeamsGalleryImage({
          isNew: true,
          url,
        });

        await fetchTeamsGalleryImages();

        enqueueSnackbar("Image uploaded successfully", {
          autoHideDuration: 3000,
          variant: "success",
        });
      } catch (error) {
        console.error("Error uploading image:", error);
        enqueueSnackbar("Failed to upload image", {
          autoHideDuration: 3000,
          variant: "error",
        });
      } finally {
        setLoading(false);
        setUploadingImageId(null);
      }
    },
    [updateTeamsGalleryImage, fetchTeamsGalleryImages, enqueueSnackbar]
  );

  const handleImageRemove = useCallback(
    async (index) => {
      try {
        setLoading(true);

        if (index >= teamsGalleryImages.length) {
          const newIndex = index - teamsGalleryImages.length;
          const newImagesList = [...newImages];
          newImagesList.splice(newIndex, 1);
          setNewImages(newImagesList);
        } else {
          // Removing existing image
          const imageId = teamsGalleryImages[index].id;

          const deletePayload = {
            imageId,
            method: "DELETE",
          };

          await deleteTeamsGalleryImage(deletePayload);

          // Refreshing after successful deletion
          await fetchTeamsGalleryImages();
          enqueueSnackbar("Image deleted successfully", {
            autoHideDuration: 3000,
            variant: "success",
          });
        }
      } catch (error) {
        console.error("Error removing image:", error);
        enqueueSnackbar("Failed to remove image", {
          autoHideDuration: 3000,
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [
      deleteTeamsGalleryImage,
      fetchTeamsGalleryImages,
      enqueueSnackbar,
      teamsGalleryImages,
      newImages,
    ]
  );

  const handleDeleteClick = (index) => {
    setImageToDelete(index);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    if (imageToDelete !== null) {
      await handleImageRemove(imageToDelete);
      setImageToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setImageToDelete(null);
  };

  if (isTeamsGalleryImagesLoading) {
    return (
      <GalleryContainer elevation={3}>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </GalleryContainer>
    );
  }

  return (
    <GalleryContainer elevation={3}>
      <Typography gutterBottom align="center" component="h1" variant="h4">
        Teams Gallery
      </Typography>
      <Typography
        paragraph
        align="center"
        color="textSecondary"
        variant="body1"
      >
        Upload and manage background images for Teams
      </Typography>
      <Box display="flex" justifyContent="center" mb={2} mt={2}>
        <ImageUpload
          buttonProps={{
            children:
              loading && uploadingImageId === "direct-upload" ? (
                <>
                  <CircularProgress
                    color="inherit"
                    size={16}
                    style={{ marginRight: 8 }}
                  />
                  Uploading...
                </>
              ) : (
                <>
                  <AddIcon style={{ marginRight: 8 }} />
                  Upload Image
                </>
              ),
            color: "primary",
            disabled: loading && uploadingImageId === "direct-upload",
            variant: "contained",
          }}
          customPathInfo={{ cid: "67df6292-c0a7-4831-a94e-61b6ea73c990" }}
          hidePreview={true}
          showRemoveButton={false}
          showUploadArea={false}
          onChange={handleDirectUpload}
        />
      </Box>
      <Grid container spacing={2}>
        {teamsGalleryImages?.map((image, index) => (
          <Grid
            key={image?.id || index}
            item
            justifyContent="center"
            md={2}
            sm={6}
            style={{
              marginBlock: "0.5rem",
            }}
            xs={6}
          >
            <ImageCard elevation={2}>
              <ImageContainer
                center={true}
                styles={{
                  borderRadius: "8px",
                  height: 120,
                  width: 120,
                }}
              >
                <img
                  alt={`Gallery ${index + 1}`}
                  src={image?.url}
                  style={{
                    aspectRatio: "16/9",
                    borderRadius: "8px",
                    height: "120px",
                    objectFit: "contain",
                    width: "100%",
                  }}
                />
              </ImageContainer>
              <ImageInfo>
                <ButtonContainer>
                  <Button
                    color="secondary"
                    size="small"
                    variant="outlined"
                    onClick={() => handleDeleteClick(index)}
                  >
                    Delete
                  </Button>
                </ButtonContainer>
              </ImageInfo>
            </ImageCard>
          </Grid>
        ))}
      </Grid>

      <Dialog
        aria-describedby="delete-dialog-description"
        aria-labelledby="delete-dialog-title"
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle id="delete-dialog-title">Delete Image</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Tips that are using this image as their background will continue to
            display it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="outlined"
            onClick={handleDeleteCancel}
          >
            Cancel
          </Button>
          <Button
            autoFocus
            color="primary"
            variant="contained"
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </GalleryContainer>
  );
};

export default TeamsGallery;
